import { useEffect } from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { Schema } from 'effect'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Building2, Shield, ShieldPlus, User, UserRound, Wallet } from 'lucide-react'
import { useCheckInSend } from '../context'
import {
  PatientBasicsSchema,
  validateGenderSelected,
  validateInsurerNameRequired,
  type PatientBasicsData,
  type Gender,
  type Insurance,
} from '../schema'
import { useAudio } from '../../../../../lib/use-audio'
import { getFormErrorMessage } from '../../../../../lib/forms/form-errors'
import { KioskStep } from '../../../../../components/kiosk-step'
import { KioskTextField } from '../../../../../components/kiosk-field'
import { KioskDateOfBirthField } from '../../../../../components/kiosk-date-of-birth-field'
import { normalizeDateOfBirthForApi } from '../../../components/DateOfBirthInput'
import { CheckInChoiceGroup, type CheckInChoiceOption } from './check-in-choice-group'
import { GENDER_CODES, insuranceTypeLabelKey } from './registration-display'

const INSURER_NAME_FIELD_ID = 'checkin-register-insurer-name'

const INSURANCE_ICONS: Readonly<Record<Insurance, typeof ShieldPlus>> = {
  GKV: ShieldPlus,
  PKV: Shield,
  SELFPAY: Wallet,
}

const DEFAULT_VALUES: PatientBasicsData = {
  familyName: '',
  givenName: '',
  dob: '',
  gender: '',
  insurance: 'GKV',
  insurerName: '',
}

/** A field error, shown once the patient left the field or tried to continue (KC-75). */
function visibleError(
  meta: { readonly isBlurred: boolean; readonly errors: readonly unknown[] },
  submitted: boolean
): unknown {
  return (meta.isBlurred || submitted) && meta.errors.length > 0 ? meta.errors[0] : undefined
}

/**
 * Patient registration basics step: name, date of birth, gender and insurance
 * type. "Weiter" is the footer action; errors appear after leaving a field or
 * pressing "Weiter", never when a field is revealed.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <PatientRegisterBasicsStep />
 * ```
 */
export function PatientRegisterBasicsStep() {
  const { t } = useTranslation()
  const send = useCheckInSend()

  // Play audio on mount
  useAudio('checkIn-patientRegisterBasics')

  const form = useForm({
    defaultValues: DEFAULT_VALUES,
    validators: {
      onChange: Schema.toStandardSchemaV1(PatientBasicsSchema)
    },
    onSubmit: async ({ value }) => {
      if (value.gender === '') return
      send({
        type: 'REGISTRATION_BASICS_NEXT',
        payload: {
          familyName: value.familyName.trim(),
          givenName: value.givenName.trim(),
          dob: normalizeDateOfBirthForApi(value.dob),
          gender: value.gender,
          insurance: value.insurance,
          insurerName: value.insurance === 'PKV' ? value.insurerName : undefined
        }
      })
    }
  })

  const insurance = useStore(form.store, (state) => state.values.insurance)
  const submitted = useStore(form.store, (state) => state.submissionAttempts > 0)

  useEffect(
    function revealInsurerNameField() {
      if (insurance !== 'PKV') return
      document
        .getElementById(INSURER_NAME_FIELD_ID)
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    },
    [insurance]
  )

  const genderOptions: readonly CheckInChoiceOption<Gender>[] = GENDER_CODES.map((code) => ({
    value: code,
    label: t(`common.gender.${code}`),
    testId: `frontdesk-register-basics-gender-${code.toLowerCase()}-btn`,
  }))
  const insuranceOptions: readonly CheckInChoiceOption<Insurance>[] = (
    ['GKV', 'PKV', 'SELFPAY'] as const
  ).map((value) => ({
    value,
    label: t(insuranceTypeLabelKey(value)),
    icon: INSURANCE_ICONS[value],
    testId: `frontdesk-register-basics-insurance-${value.toLowerCase()}-btn`,
  }))

  return (
    <KioskStep
      width="narrow"
      title={t('self-service.check-in.register-basics.title')}
      lead={t('self-service.check-in.register-basics.subtitle')}
      primaryAction={{
        label: t('common.next'),
        icon: ArrowRight,
        onClick: () => void form.handleSubmit(),
        testId: 'register-basics-next',
      }}
    >
      <form
        data-testid="frontdesk-register-basics-form"
        className="flex flex-col gap-6"
        onSubmit={(event) => {
          event.preventDefault()
          void form.handleSubmit()
        }}
      >
        <form.Field name="familyName">
          {(field) => {
            const error = visibleError(field.state.meta, submitted)
            return (
              <KioskTextField
                id="checkin-register-family-name"
                testId="frontdesk-register-basics-family-name-input"
                label={t('self-service.check-in.register-basics.family-name')}
                icon={UserRound}
                required
                autoComplete="family-name"
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                error={error === undefined ? undefined : t(getFormErrorMessage(error))}
              />
            )
          }}
        </form.Field>

        <form.Field name="givenName">
          {(field) => {
            const error = visibleError(field.state.meta, submitted)
            return (
              <KioskTextField
                id="checkin-register-given-name"
                testId="frontdesk-register-basics-given-name-input"
                label={t('self-service.check-in.register-basics.given-name')}
                icon={User}
                required
                autoComplete="given-name"
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                error={error === undefined ? undefined : t(getFormErrorMessage(error))}
              />
            )
          }}
        </form.Field>

        <form.Field name="dob">
          {(field) => {
            const error = visibleError(field.state.meta, submitted)
            return (
              <KioskDateOfBirthField
                id="checkin-register-dob"
                testId="frontdesk-register-basics-dob-input"
                pickerTestId="frontdesk-register-basics-dob-picker-btn"
                label={t('self-service.check-in.register-basics.dob')}
                description={t('self-service.check-in.dob-format-hint')}
                required
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                error={error === undefined ? undefined : t(getFormErrorMessage(error))}
              />
            )
          }}
        </form.Field>

        <form.Field
          name="gender"
          validators={{ onChange: ({ value }) => validateGenderSelected(value) }}
        >
          {(field) => (
            <CheckInChoiceGroup
              legend={t('self-service.check-in.register-basics.gender')}
              options={genderOptions}
              value={field.state.value}
              onChange={(gender) => field.handleChange(gender)}
              error={
                submitted && field.state.value === ''
                  ? t('self-service.check-in.register-basics.gender-required')
                  : undefined
              }
              errorTestId="frontdesk-register-basics-gender-error"
            />
          )}
        </form.Field>

        <form.Field name="insurance">
          {(field) => (
            <CheckInChoiceGroup
              legend={t('self-service.check-in.register-basics.insurance-type')}
              options={insuranceOptions}
              value={field.state.value}
              onChange={(value) => field.handleChange(value)}
            />
          )}
        </form.Field>

        {insurance === 'PKV' ? (
          <form.Field
            name="insurerName"
            validators={{
              onChange: ({ value }) => validateInsurerNameRequired(value)
            }}
          >
            {(field) => {
              const error = visibleError(field.state.meta, submitted)
              return (
                <KioskTextField
                  id={INSURER_NAME_FIELD_ID}
                  testId="frontdesk-register-basics-insurer-name-input"
                  label={t('self-service.check-in.register-basics.insurer-name')}
                  description={t('self-service.check-in.register-basics.insurer-name-placeholder')}
                  icon={Building2}
                  required
                  value={field.state.value ?? ''}
                  onChange={field.handleChange}
                  onBlur={field.handleBlur}
                  error={error === undefined ? undefined : t(getFormErrorMessage(error))}
                />
              )
            }}
          </form.Field>
        ) : null}
      </form>
    </KioskStep>
  )
}
